import { Button, EmptyState, Spinner, Tabs, TextField } from "@hexling/ui";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { PageTitle } from "../../components/Layout";
import {
  useBookmarks,
  useContent,
  useListItems,
  useLists,
  useNotes,
  useServices,
} from "../../data/AppData";
import { formatDay } from "../../lib/dates";
import { categoryInfo } from "../library/categories";

export type GrimoireTab = "notes" | "lists" | "bookmarks";

export function GrimoirePage({
  tab = "notes",
  onTab,
}: {
  tab?: GrimoireTab;
  onTab?: (t: GrimoireTab) => void;
}) {
  const [current, setCurrent] = useState<GrimoireTab>(tab);
  const change = (t: string) => {
    setCurrent(t as GrimoireTab);
    onTab?.(t as GrimoireTab);
  };
  return (
    <>
      <PageTitle
        title="Grimoire"
        subtitle="Your notes, lists and bookmarks. Saved on this device; synced if you turn it on."
      />
      <Tabs
        label="Grimoire sections"
        value={current}
        onChange={change}
        tabs={[
          { id: "notes", label: "Notes", content: <NotesTab /> },
          { id: "lists", label: "Lists", content: <ListsTab /> },
          { id: "bookmarks", label: "Bookmarks", content: <BookmarksTab /> },
        ]}
      />
    </>
  );
}

function NotesTab() {
  const notes = useNotes();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  if (notes.isPending) return <Spinner />;
  const all = notes.data ?? [];
  const term = q.trim().toLowerCase();
  const shown = term
    ? all.filter((n) => `${n.title} ${n.body} ${n.tags.join(" ")}`.toLowerCase().includes(term))
    : all;
  const create = () => navigate({ to: "/grimoire/notes/$id", params: { id: "new" } });

  if (!all.length) {
    return (
      <EmptyState
        glyph="✒️"
        title="No notes yet"
        action={<Button onClick={create}>Write your first note</Button>}
      >
        Record rituals, dreams, readings and recipes you've tweaked.
      </EmptyState>
    );
  }
  return (
    <>
      <div className="toolbar">
        <Button onClick={create}>New note</Button>
      </div>
      <TextField
        label="Search notes"
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <ul className="label-list">
        {shown.map((n) => (
          <li key={n.id}>
            <Link className="label-row" to="/grimoire/notes/$id" params={{ id: n.id }}>
              <span>
                <span className="label-row__name">{n.title}</span>
                <span className="label-row__blurb">{n.body.slice(0, 90) || "No text yet"}</span>
              </span>
              <span className="label-row__meta">
                {formatDay(new Date(n.occurredOn ? `${n.occurredOn}T12:00:00` : n.updatedAt), {
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {!shown.length && <p className="hx-muted">No notes match "{q.trim()}".</p>}
    </>
  );
}

function ListsTab() {
  const lists = useLists();
  const items = useListItems().data ?? [];
  const { user } = useServices();
  const [name, setName] = useState("");
  if (lists.isPending) return <Spinner />;
  const all = lists.data ?? [];

  const create = async () => {
    if (!name.trim()) return;
    await user.saveList({ name: name.trim(), description: "", icon: "" });
    setName("");
  };

  return (
    <>
      <form
        className="inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          void create();
        }}
      >
        <TextField
          label="New list"
          placeholder="Protection herbs, Samhain shopping…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
        />
        <Button type="submit" disabled={!name.trim()}>
          Create list
        </Button>
      </form>
      {all.length ? (
        <ul className="label-list">
          {all.map((l) => (
            <li key={l.id}>
              <Link className="label-row" to="/grimoire/lists/$id" params={{ id: l.id }}>
                <span className="label-row__name">
                  {l.icon ? `${l.icon} ` : ""}
                  {l.name}
                </span>
                <span className="label-row__meta">
                  {items.filter((i) => i.listId === l.id).length}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState glyph="📜" title="No lists yet">
          Make a list above, or use "Add to list" on any library entry.
        </EmptyState>
      )}
    </>
  );
}

function BookmarksTab() {
  const marks = useBookmarks();
  const index = useContent().data;
  if (marks.isPending) return <Spinner />;
  const all = marks.data ?? [];
  if (!all.length) {
    return (
      <EmptyState
        glyph="🔖"
        title="No bookmarks yet"
        action={<Link to="/library">Browse the library</Link>}
      >
        Tap "Bookmark" on anything in the library to keep it close.
      </EmptyState>
    );
  }
  return (
    <ul className="label-list">
      {all.map((b) => {
        const item = index?.get(b.ref.category, b.ref.slug);
        return (
          <li key={b.id}>
            <Link
              className="label-row"
              to="/library/$category/$slug"
              params={{ category: b.ref.category, slug: b.ref.slug }}
            >
              <span className="label-row__glyph" aria-hidden="true">
                {categoryInfo(b.ref.category)?.glyph}
              </span>
              <span className="label-row__name">{item?.name ?? b.ref.slug}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
